import cn from "classnames";
import { FC, useEffect, useMemo, useState } from "react";
import { isDesktop, isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useSetGameBoxType } from "../../hoc/useSetGameBoxType";
import { requestRaces } from "../../store/actionCreators/races";
import {
  getCasinoTournamentNextInstances,
  getRacesAccountResult,
} from "../../store/selectors";
import Icon from "../common/Icon";
import CountDownTimer from "../CountDownTimer";
import ProgressBar from "../ProgressBar";

import { TFunction } from "i18next";
import "./styles.scss";

const drawRaceProgress = (
  mission: {
    expire_time: number;
  },
  t: TFunction<"translation">,
  onRaceFinish: () => void
) => {
  return (
    <div className="race-progress__timer">
      <span className="race-progress__timer__label">{t("endsIn")}</span>
      <CountDownTimer
        countdownTimestampMs={mission?.expire_time}
        withoutLabels={true}
        withDots={true}
        size="xs"
        withSkeleton={true}
        onFinish={onRaceFinish}
      />
    </div>
  );
};

interface GameBoxCategoryProps {
  item: any;
  selected: string;
  gameBoxWidgetVisibilityHandler?: (st?: boolean) => void;
  isWidget?: boolean;
  setIsOpen?: (st?: boolean) => void;
  storedRaceValue?: string | null;
}

const GameBoxCategory: FC<GameBoxCategoryProps> = ({
  item,
  selected,
  gameBoxWidgetVisibilityHandler,
  isWidget,
  setIsOpen,
  storedRaceValue,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const setGameBoxType = useSetGameBoxType();
  const accountResult = useSelector(getRacesAccountResult);
  let mission: any = useSelector(item?.mission);
  const casinoTournamentNextInstances: any = useSelector(
    getCasinoTournamentNextInstances
  );
  if (mission?.submissions) {
    mission = mission.submissions[mission?.current_index - 1];
  }
  const [validMission, setValidMission] = useState<boolean>(false);
  const [isRaceFinished, setIsRaceFinished] = useState<boolean>(false);
  const tabClickAudio = useMemo(() => new Audio('static/sounds/click.mp3'), []);

  const onRaceFinish = () => {
    dispatch(requestRaces());
    setIsRaceFinished(true);
  };

  useEffect(() => {
    if (!mission) {
      setValidMission(false);
    } else if (
      item.type === "tournament" &&
      casinoTournamentNextInstances.length
    ) {
      setValidMission(false);
    } else if ((item.type === "funBonus" || item.type === "miniGames") && isWidget) {
      setValidMission(false);
    } else {
      setValidMission(true);
    }
  }, [item, mission, casinoTournamentNextInstances, isWidget]);

  const firstMission = mission?.length ? mission[0] : mission;

  const multipleRaces =
    item.title === "race" &&
    mission.length > 1 &&
    mission.find((data: { id: number }) => data.id === Number(storedRaceValue));

  const getTitle = () => {
    return t(`gameBoxCategories.${item?.title}`);
  };

  const wagered =
    firstMission?.status === "completed"
      ? firstMission?.trigger?.points
      : firstMission?.progress ?? "0";

  const total = firstMission?.status
    ? firstMission?.trigger?.points
    : firstMission?.goal ?? firstMission?.balance;

  const tabClick = () => {
    setGameBoxType(item?.title);
    tabClickAudio.currentTime = 0;
    tabClickAudio.play().catch(console.error)
    localStorage.removeItem('missionCompleted');
    window.parent.postMessage(
      {
        type: "GAMEBOX_CATEGORY_CHANGE_TRIGGERED",
        data: { navigation: "horizontal" },
      },
      "*"
    );

    isMobile &&
      gameBoxWidgetVisibilityHandler &&
      gameBoxWidgetVisibilityHandler(true);
    setIsOpen && setIsOpen(false);
  };

  return (
    <div
      className={cn("game-box-widget-tab-item__container", {
        selected: item?.title === selected,
        mobile: isMobile,
      })}
    >
      <div
        className={`game-box-widget-tab-item ${firstMission?.status} ${item?.title}`}
        onClick={tabClick}
      >
        <>
          <div className="game-box-widget-tab-item__name">
            <Icon
              img={`/static/images/${item?.img}/${item.img}.png`}
              size={isDesktop ? "md" : "sm"}
            />
            <span>{getTitle()}</span>
            {item.title === "race" && isDesktop && accountResult?.rank && (
              <div className="race-rank">
                {t("you")} {accountResult?.rank}
              </div>
            )}
          </div>
          {item.type === "funBonus" && isWidget && (
            <img
              src={`/static/images/${item?.img}/gropedBonus.png`}
              alt="gropedBonus"
            />
          )}
          {item.type === "miniGames" && isWidget && (
              <img
                  src={`/static/images/${item?.img}/groupedMiniGames.png`}
                  className="grouped-mini-games"
                  alt="groupedMiniGames"
              />
          )}
          {validMission && (
            <>
              {item.title === "race" ? (
                <div
                  className={`progress-with-percentage ${firstMission?.status}`}
                >
                  {firstMission?.status === "completed" || isRaceFinished
                    ? "100%"
                    : drawRaceProgress(
                        multipleRaces || firstMission,
                        t,
                        onRaceFinish
                      )}
                </div>
              ) : (
                <>
                  <div className={`game-box-widget-tab-item__progress-bar`}>
                    <ProgressBar
                      data={firstMission}
                      percentage={isWidget ? 94 : 93}
                    />
                  </div>
                  <div className="game-box-widget-tab-item__wager">
                    <p>
                      {isDesktop && t("wager") + ":"} {wagered} /{" "}
                      <span>{total > 999 ? total / 1000 + "K" : total}</span>
                    </p>
                    {isDesktop && (
                      <img
                        src={`/static/images/${item?.img}/${item.wagerImg}.png`}
                        width={item.width}
                        alt={item.wagerImg}
                      />
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default GameBoxCategory;
